import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useLocation } from "react-router-dom";
import takyonLightLogo from "../../../assets/img/icons/TakyonLight.svg";
import dairectLogoWordmark from "../../../assets/img/icons/dairect-logo-wordmark.svg";
import {
  getLocaleLanguage,
  setLocaleLanguage,
} from "../../../assets/Services/i18next";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import Icon from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { SMALL_SCREEN } from "../../../assets/Utils/generic";
import { LANG } from "../../../config/Lang";
import getLink, { LINK_TYPE } from "../../../config/Links";
import { useAppDispatch } from "../../../redux/redux";
import {
  selectProfileMenuVisibility,
  setProfileMenu,
} from "../../../redux/slices/appSlice";
import {
  selectSellerPendingOffersCount,
  selectUser,
} from "../../../redux/slices/userSlice";
import Search from "../../pages/Directsearch/Search";
import openAuthModal from "../Auth/Auth";
import IconFilter from "../../../assets/icons/IconFilter";
import { openModal } from "../../../assets/Utils/modal";
import FiltersModal from "../../pages/Directsearch/FiltersModal";
import { useSearch } from "../../hooks/useSearch";

import { ANALYTICS_SOURCE } from "../../../config/analytics";

interface Props {
  lang?: LANG;
  setLang?: (lang: LANG) => void;
}

function NavLang({ lang, setLang }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const changeLocaleLanguageSelect = (selectedLang: LANG) => {
    fireTagManagerEvent("change_language", {
      original: lang,
      final: selectedLang,
    });
    setLang?.(selectedLang);
    setLocaleLanguage(selectedLang);
    setIsOpen(false);
  };

  const initAsync = async () => {
    await getLocaleLanguage();
  };

  useEffect(() => {
    initAsync();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onWindowClick = () => setIsOpen(false);
    window.addEventListener("click", onWindowClick);

    return () => {
      window.removeEventListener("click", onWindowClick);
    };
  }, [isOpen]);

  return (
    <div className="position-relative">
      <button
        type="button"
        className="d-flex align-items-center cursor-pointer gap-1 bg-transparent border-0 p-0"
        onClick={(e) => {
          setIsOpen(!isOpen);
          e.stopPropagation();
        }}
      >
        <Icon
          size={20}
          icon={isOpen ? "expand_less" : "expand_more"}
          type="symbol"
          color="#ffffff"
        />
        <p className="fs-body-sm text-white m-0">{lang?.toUpperCase()}</p>
      </button>
      {isOpen ? (
        <div
          style={{
            top: "140%",
            right: 0,
            zIndex: 99999,
            backgroundColor: "rgba(9, 17, 18, 0.98)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
          }}
          className="position-absolute p-3 gap-2 d-flex flex-column align-items-end rounded-3 shadow"
          onClick={(e) => e.stopPropagation()}
        >
          {Object.keys(LANG).map((langKey, key) => {
            const langValue = LANG[langKey as keyof typeof LANG];

            return (
              <p
                onClick={() => {
                  changeLocaleLanguageSelect(langValue);
                  setIsOpen(false);
                }}
                className="m-0 bodytext-sm cursor-pointer text-nowrap text-white hover-underline"
                key={key}
              >
                {langValue.toUpperCase()}
              </p>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface InnerNavProps {
  showActions?: boolean;
  light?: boolean;
}

const NAV_BG = "rgba(9, 17, 18, 0.92)";
const NAV_BORDER = "1px solid rgba(255, 255, 255, 0.1)";
const NAV_ICON_BORDER = "1px solid rgba(255, 255, 255, 0.3)";
const NAV_ACTION_BORDER = "1px solid rgba(255, 255, 255, 0.2)";

export function InnerNav({ showActions = true }: InnerNavProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });
  const [lang, setLang] = useState<LANG | undefined>();
  const offersPendingCount = useSelector(selectSellerPendingOffersCount);
  const isProfileMenuOpened = useSelector(selectProfileMenuVisibility);
  const user = useSelector(selectUser);
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isDS = pathname === "/directsearch/search";

  const { goToSearchUrl, searchParameters } = useSearch();

  useEffect(() => {
    const initAsync = async () => {
      const l = await getLocaleLanguage();

      const langValue: LANG | undefined = l as LANG | undefined;
      setLang(langValue);
    };
    initAsync();
  }, []);

  const numberOfActiveFilters = useMemo(
    () =>
      (searchParameters.filters
        ? Object.keys(searchParameters.filters).reduce((acc, key) => {
            const typedKey = key as keyof typeof searchParameters.filters;

            if (
              [
                "certifiedOnly",
                "isLoyaltyEnabled",
                "minReviewsScore",
                "onlyHotels",
              ].includes(key)
            ) {
              if (searchParameters.filters?.[typedKey] !== undefined) {
                return acc + 1;
              }

              return acc;
            }

            if (["stars", "facilities"].includes(key)) {
              if (searchParameters.filters?.[typedKey] !== undefined) {
                return (
                  //@ts-ignore
                  acc +
                    (searchParameters.filters?.[typedKey] as Array<unknown>)
                      ?.length ?? 0
                );
              }

              return acc;
            }

            return acc;
          }, 0)
        : 0) + 0,
    [searchParameters.filters],
  );

  return (
    <div
      className="w100"
      style={{
        backdropFilter: "blur(15px)",
        backgroundColor: NAV_BG,
        borderBottom: NAV_BORDER,
      }}
    >
      <div style={{ padding: isSmallScreen ? "0 20px" : "0 40px" }}>
        <div
          className="d-flex justify-content-between align-items-center w100 p-0"
          style={{ minHeight: isSmallScreen ? "72px" : "60px" }}
        >
          <button
            type="button"
            className="bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => {
              const wallPath = getLink(LINK_TYPE.WALL);
              if (window.location.pathname !== wallPath) {
                navigate(wallPath);
              } else {
                window.scrollTo(0, 0);
              }
              dispatch(setProfileMenu(false));
            }}
          >
            {isSmallScreen ? (
              <div className="d-flex flex-column justify-content-center gap-1">
                <img
                  src={takyonLightLogo}
                  alt="Takyon"
                  style={{ height: "20px", width: "69px" }}
                />
                <img
                  src={dairectLogoWordmark}
                  alt="dAirect"
                  style={{ height: "14px", width: "57px" }}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                <img
                  src={takyonLightLogo}
                  alt="Takyon"
                  style={{ height: "20px", width: "69px" }}
                />
                <span
                  style={{
                    width: "1px",
                    height: "20px",
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                  }}
                />
                <img
                  src={dairectLogoWordmark}
                  alt="dAirect"
                  style={{ height: "24px", width: "98px" }}
                />
              </div>
            )}
          </button>
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            {isDS && !isSmallScreen && (
              <div className="flex items-center gap-2">
                <Search
                  mode={isSmallScreen ? "mobile" : "desktop"}
                  showFilters={false}
                  isNav
                  parent="nav"
                />
                <div className="relative size-[3.125rem]">
                  {numberOfActiveFilters > 0 && (
                    <div className="absolute w-2 h-5 bg-[#F3F3F3] -translate-x-full top-1/2 -translate-y-1/2 before:content-[''] before:absolute before:size-2 before:bg-white before:rounded-full before:left-1/2 before:-translate-x-1/2 before:-translate-y-2/3 after:content-[''] after:absolute after:size-2 after:bg-white after:rounded-full after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:translate-y-2/3" />
                  )}
                  <button
                    type="button"
                    className="size-12 rounded-2xl bg-[#F3F3F3] flex items-center justify-center border-none"
                    onClick={() => {
                      openModal(
                        {
                          content: (
                            <FiltersModal
                              onFilter={({ filters }) => {
                                goToSearchUrl(
                                  { filters },
                                  {
                                    triggerTagManagerEvent: false,
                                    source: ANALYTICS_SOURCE.LIST,
                                  },
                                );
                              }}
                              source={ANALYTICS_SOURCE.LIST}
                            />
                          ),
                          className:
                            "rounded-t-3xl self-end h-[calc(100dvh-1rem)] lg:h-auto lg:self-center lg:rounded-3xl",
                        },
                        { hideModalCloseBtn: true },
                      );
                    }}
                  >
                    <IconFilter className="size-4" />
                  </button>
                  {numberOfActiveFilters > 0 && (
                    <div className="absolute top-0 right-0 translate-x-0.5 -translate-y-0.5 aspect-square rounded-full bg-black size-5 text-[0.625rem] font-medium text-white flex items-center justify-center text-center">
                      {numberOfActiveFilters}
                    </div>
                  )}
                </div>
              </div>
            )}
            {(isSmallScreen && !isProfileMenuOpened) || !isSmallScreen ? (
              <>
                <NavLang lang={lang} setLang={setLang} />

                {showActions ? (
                  <>
                    {!pathname.includes(getLink(LINK_TYPE.PROFILE)) && (
                      <Button
                        className="btn-nav"
                        style={{
                          backgroundColor: "transparent",
                          border: NAV_ACTION_BORDER,
                          color: "#FFFFFF",
                          height: "40px",
                          padding: isSmallScreen ? "8px 10px" : "8px 12px",
                          marginTop: "0",
                          marginBottom: "0",
                          borderRadius: "12px",
                          boxShadow: "none",
                          minWidth: isSmallScreen ? "104px" : "113px",
                        }}
                        onClick={() => {
                          fireTagManagerEvent("click_become_partner", {
                            source: "nav",
                          });
                          navigate(getLink(LINK_TYPE.SECONDARY_MARKET));
                        }}
                        variant="blank"
                        textClass="fs-body-sm text-white"
                        text={t("secondary.badge")}
                      />
                    )}

                    <Button
                      className="btn-nav p-0"
                      size="sm"
                      variant="blank"
                      textClass="fs-body-sm text-white"
                      iconStyle={{
                        fill: "#ffffff",
                      }}
                      style={{
                        backgroundColor: "transparent",
                        borderRadius: "100px",
                        width: "40px",
                        height: "40px",
                        padding: user ? "8px 8px" : "0px",
                        marginTop: "0",
                        marginBottom: "0",
                        border: NAV_ICON_BORDER,
                        boxShadow: "none",
                        color: "#FFFFFF",
                      }}
                      onClick={() => {
                        if (user) {
                          dispatch(setProfileMenu(true));
                        }
                        if (
                          user &&
                          user.emailVerified &&
                          typeof user.marketingEmails !== "undefined" &&
                          user.firstName &&
                          user.secondName &&
                          user.phone &&
                          user.phonePrefix &&
                          user.dateOfBirth
                        ) {
                          navigate(getLink(LINK_TYPE.PROFILE_WALLET));
                        } else {
                          openAuthModal();
                        }
                      }}
                      iconSize={24}
                      icon={user ? "" : "login"}
                      notification={
                        offersPendingCount > 0 ? offersPendingCount : undefined
                      }
                      text={
                        user
                          ? String(user.firstName?.[0] ?? "").toUpperCase() +
                            String(user.secondName?.[0] ?? "").toUpperCase()
                          : ""
                      }
                    />
                  </>
                ) : null}
              </>
            ) : (
              <button
                type="button"
                className="bg-transparent border-0 p-0 d-flex align-items-center justify-content-center"
                onClick={() => dispatch(setProfileMenu(false))}
              >
                <Icon icon="close" color="#ffffff" size={22} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Nav() {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });

  return (
    <nav className="top-0 fixed w-full z-20" id="navbar">
      <button
        type="button"
        className="w-100 d-flex align-items-center justify-content-center border-0"
        style={{
          height: isSmallScreen ? "32px" : "28px",
          padding: "0 12px",
          gap: "4px",
          background: "#9AFEE3",
          borderBottom: NAV_BORDER,
        }}
        onClick={() => window.open("https://dairect.io", "_blank")}
      >
        <p
          className="m-0"
          style={{
            color: "#091112",
            fontWeight: 400,
            fontSize: isSmallScreen ? "14px" : "16px",
            lineHeight: "20px",
          }}
        >
          {t("ds.home_rebrand_title")},
        </p>
        <span
          className="d-flex align-items-center"
          style={{
            color: "#091112",
            gap: "4px",
            fontWeight: 600,
            fontSize: isSmallScreen ? "14px" : "16px",
            lineHeight: "20px",
          }}
        >
          {t("ds.home_rebrand_section4_button")}
          <Icon icon="north_east" size={18} type="symbol" color="#091112" />
        </span>
      </button>
      <InnerNav />
    </nav>
  );
}
